import type {
    BootstrapCommerceSessionResponse,
    CommerceDataSource,
    CommerceSession,
} from "@teqtank/commerce-contracts";
import type { CommerceTransport } from "@teqtank/commerce-transport";
import { CommerceError } from "../errors/commerce-error.js";
import { bootstrapCommerceSessionOperation } from "../operations/bootstrap-commerce-session-operation.js";
import {
    readPersistedCommerceSession,
    writePersistedCommerceSession,
} from "./browser-commerce-session.js";

export async function bootstrapCommerceSession(
    transport: CommerceTransport,
    commerceKey: string,
    dataSource: CommerceDataSource,
    correlationId: string,
): Promise<CommerceSession> {
    let response: BootstrapCommerceSessionResponse;
    const persistedToken = readPersistedCommerceSession({
        commerceKey,
        dataSource,
        transport,
    });

    try {
        response = await transport.execute(
            bootstrapCommerceSessionOperation,
            {
                commerceKey,
            },
            {
                correlationId,
                commerceKey,
                dataSource,
                ...(persistedToken === undefined ? {} : { sessionToken: persistedToken }),
            },
        );
    } catch (cause) {
        console.error("Commerce connection failed:", cause);

        throw new CommerceError(
            "session-bootstrap-failed",
            "Teqtank Commerce could not establish a Commerce session.",
            {
                commerceKey,
                transportName: transport.name,
                correlationId,
                cause,
            },
        );
    }

    const session = resolveCommerceSession(response, commerceKey, dataSource);
    writePersistedCommerceSession({ commerceKey, dataSource, transport }, session.token);
    return session;
}

function resolveCommerceSession(
    response: BootstrapCommerceSessionResponse | null | undefined,
    requestedCommerceKey: string,
    requestedDataSource: CommerceDataSource,
): CommerceSession {
    if (response === null || response === undefined || typeof response !== "object") {
        throw createInvalidSessionError(
            requestedCommerceKey,
            "The Commerce session response was missing.",
        );
    }

    if (typeof response.sessionToken !== "string" || response.sessionToken.trim().length === 0) {
        throw createInvalidSessionError(
            requestedCommerceKey,
            "The Commerce session token was missing or empty.",
        );
    }

    if (typeof response.commerceKey !== "string" || response.commerceKey.trim().length === 0) {
        throw createInvalidSessionError(
            requestedCommerceKey,
            "The Commerce session commerceKey was missing or empty.",
        );
    }

    const commerceKey = response.commerceKey.trim();

    if (commerceKey !== requestedCommerceKey) {
        throw createInvalidSessionError(
            requestedCommerceKey,
            "The Commerce session commerceKey did not match the requested key.",
            {
                receivedCommerceKey: commerceKey,
            },
        );
    }

    if (response.dataSource !== requestedDataSource) {
        throw createInvalidSessionError(
            requestedCommerceKey,
            "The Commerce session dataSource did not match the requested source.",
            {
                requestedDataSource,
                receivedDataSource: response.dataSource,
            },
        );
    }

    return Object.freeze({
        token: response.sessionToken.trim(),
        commerceKey,
        dataSource: requestedDataSource,
    });
}

function createInvalidSessionError(
    commerceKey: string,
    message: string,
    details: Readonly<Record<string, unknown>> = {},
): CommerceError {
    return new CommerceError("session-invalid", message, {
        commerceKey,
        ...details,
    });
}
